import React, { useState, useEffect } from 'react';
import GlobalButton from '@/components/ui/GlobalButton';
import ReactGA from 'react-ga4';
import useAuthStore from '@/store/Auth';
import CustomModalOverlay from '@/components/common/Custom/CustomModalOverlay/CustomModalOverlay';
import stepImg from '@/assets/images/step1-img.svg';
import WebAppService from '../../services/WebAppService';
import { getErrorMessage } from '../../utils/helpers/helpers';
import SelectInput from '../ui/SelectInput/SelectInput';
import TextArea from '../ui/TextArea/TextArea';

interface KycFormProps {
  onClose: () => void;
}

export default function KycForm({ onClose }: KycFormProps) {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState<Record<string, string>>({
    role: '',
    message: '',
    employees: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSkipSubmitting, setIsSkipSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [shaking, setShaking] = useState(false);
  const authStore = useAuthStore();
  

  const userId = authStore.user.id;

  const triggerShake = () => {
    setShaking(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setShaking(true));
    });
  };

  const updateFormData = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDropdownChange = (name: string, value: string) => {
    updateFormData(name, value);
  };

  const handleInputChange = (
    name: string,
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    updateFormData(name, event.target.value);
  };

  const steps = [
    {
      heading: 'Built Around Your Team',
      subtitle:
        'Configure smarter workflows, automate coordination, and give your team a centralized workspace.',
      fieldLabel: 'What best describes your role?',
      component: SelectInput,
      props: {
        placeholder: 'Select your primary role',
        name: 'role',
        options: [
          'Cybersecurity Analyst',
          'Data Scientist',
          'Database Administrator (DBA)',
          'Engineering',
          'IT Auditor',
          'IT Support Specialist',
          'IT Consultant',
          'Product Manager',
          'Systems Administrator',
          'Technical Writer',
          'UX/UI Designer',
        ].map((o) => ({ value: o, label: o })),
        onChange: handleDropdownChange,
      },
    },
    {
      heading: 'Built Around Your Team',
      subtitle:
        'Configure smarter workflows, automate coordination, and give your team a centralized workspace.',
      fieldLabel: 'How many people work here?',
      component: SelectInput,
      props: {
        placeholder: 'Select team size',
        name: 'employees',
        options: [
          '1-10 employees',
          '20-50 employees',
          '50-100 employees',
          '100+ employees',
        ].map((o) => ({ value: o, label: o })),
        onChange: handleDropdownChange,
      },
    },
    {
      heading: 'Built Around Your Team',
      subtitle:
        'Configure smarter workflows, automate coordination, and give your team a centralized workspace.',
      fieldLabel: 'How do you plan to use Crafter?',
      component: TextArea,
      props: {
        name: 'message',
        placeholder: 'Type your reason here',
        onChange: handleInputChange,
        maxLength: 100,
      },
    },
  ];

  const totalSteps = steps.length;
  const currentStep = steps[step];
  const currentValue = formData[currentStep.props.name] || '';

  const isLastStep = step === totalSteps - 1;
  const canContinue = isLastStep
    ? !!formData.message && formData.message.length <= 100
    : !!currentValue;

  const nextStep = () => setStep((p) => p + 1);

  const submitToServer = async (
    skipped = false,
    overrides: Partial<Record<string, string>> = {}
  ) => {
    setIsSubmitting(true);
    const merged = { ...formData, ...overrides };
    const role = merged.role || 'skipped';
    const employees = merged.employees || 'skipped';
    const message = merged.message || 'skipped';

    ReactGA.event({
      category: 'kyc',
      action: 'kyc_submit',
      label: JSON.stringify({
        uid: userId,
        company_role: role,
        employee_count: employees,
        use_reason: message,
        ...(skipped ? { step: step + 1 } : {}),
      }),
    });

    try {
      const updateFlagResponse = await WebAppService.updateFlag(userId);
      if (updateFlagResponse?.success) {
        authStore.updateUser({
          email: updateFlagResponse?.data?.email,
          role: updateFlagResponse?.data?.role,
          createdAt: updateFlagResponse?.data?.created_at,
          updatedAt: updateFlagResponse?.data?.updated_at,
          id: updateFlagResponse?.data?.id,
          organization: updateFlagResponse?.data?.organization,
          isNew: updateFlagResponse?.data?.isNew,
        });

        if (!skipped) {
        }
        setTimeout(() => onClose(), skipped ? 0 : 2000);
      }
    } catch (error) {
      setIsSubmitting(false);
      setErrorMessage(getErrorMessage(error));
      triggerShake();
      console.error(error);
    }
  };

  const handleContinue = (e: React.MouseEvent | React.FormEvent) => {
    e.preventDefault();
    if (isLastStep) {
      submitToServer(false);
    } else {
      nextStep();
    }
  };

  const handleSkip = (e: React.MouseEvent) => {
    e.preventDefault();
    const fieldName = currentStep.props.name;
    if (isLastStep) {
      setIsSkipSubmitting(true);
      submitToServer(true, { [fieldName]: 'skipped' });
    } else {
      nextStep();
    }
  };

  const handleDismiss = async () => {
    const skippedFormData: Record<string, string> = {};
    Object.keys(formData).forEach((key) => {
      skippedFormData[key] = 'skipped';
    });
    onClose();

    ReactGA.event({
      category: 'kyc',
      action: 'kyc_submit',
      label: JSON.stringify({
        uid: userId,
        company_role: skippedFormData.role,
        employee_count: skippedFormData.employees,
        use_reason: skippedFormData.message,
        step: totalSteps,
      }),
    });

    try {
      const updateFlagResponse = await WebAppService.updateFlag(userId);
      if (updateFlagResponse?.success) {
        authStore.updateUser({
          email: updateFlagResponse?.data?.email,
          role: updateFlagResponse?.data?.role,
          createdAt: updateFlagResponse?.data?.created_at,
          updatedAt: updateFlagResponse?.data?.updated_at,
          id: updateFlagResponse?.data?.id,
          organization: updateFlagResponse?.data?.organization,
          isNew: updateFlagResponse?.data?.isNew,
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const StepComponent = currentStep.component;
  const stepProps = {
    ...currentStep.props,
    label: currentStep.fieldLabel,
    value: currentValue,
    onChange: (event: any) => {
      currentStep.props.onChange(currentStep.props.name, event);
    },
  };

  return (
    <CustomModalOverlay
      open={true}
      onClose={handleDismiss}
      width="1100px"
      withCloseButton
    >
      <div className="w-full flex flex-col items-start text-gray-9">
        <div className="w-full flex items-center justify-between gap-6">
          {/* Image */}
          <div className="hidden lg:block w-full lg:w-[700px] xl:w-[900px]">
            <img
              src={stepImg as unknown as string}
              alt=""
              className="w-full h-full"
            />
          </div>

          {/* Right column */}
          <div className="w-full lg:w-[90%] flex flex-col min-h-[420px]">

            <h1 className="text-text-primary-heading font-extrabold text-[28px] text-left">
              {currentStep.heading}
            </h1>
            <p className="text-left text-sm mt-2 text-text-primary-body">
              {currentStep.subtitle}
            </p>

            <form
              className="mt-8 flex flex-col gap-3 w-full flex-1"
              onSubmit={handleContinue}
            >
              <div className="relative flex flex-col gap-3">
                <StepComponent {...(stepProps as any)} />
                {isLastStep && (
                  <div
                    className={`absolute bottom-3 right-5 text-sm ${
                      (formData.message?.length || 0) > 100
                        ? 'text-error-2'
                        : 'text-white-3'
                    }`}
                  >
                   
                  </div>
                )}
              </div>

              {errorMessage && (
                <p
                  className={`text-sm text-error-2 ${shaking ? 'shake' : ''}`}
                  onAnimationEnd={() => setShaking(false)}
                >
                  {errorMessage}
                </p>
              )}

              {/* Dots + actions */}
              <div className="mt-auto pt-8 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {steps.map((_, i) => (
                    <span
                      key={i}
                      className={`w-2 h-2 rounded-full transition-all ${
                        i === step ? 'bg-navigation-accent-1' : 'bg-gray-9/30'
                      }`}
                    />
                  ))}
                </div>

                <div className="flex items-center gap-3">
                 <div>
                 <GlobalButton
                    size="md"
                    onClick={handleSkip}
                    type="button"
                    variant="stroke"
                    className={isSubmitting ? 'opacity-50 pointer-events-none' : ''}
                  >
                    {isLastStep ? 'Skip' : 'Skip for now'}
                  </GlobalButton>
                 </div>
                  <div>
                  <GlobalButton
                    onClick={handleContinue}
                    variant="purple"
                    size="md"
                    state={
                      isSubmitting || !canContinue ? 'inactive' : 'default'
                    }
                    type="submit"
                  >
                    {isLastStep
                      ? isSubmitting && !isSkipSubmitting
                        ? 'Submitting'
                        : 'Submit'
                      : 'Continue'}
                  </GlobalButton>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </CustomModalOverlay>
  );
}
